import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ExternalLink,
  ImageIcon,
  RefreshCw,
  ScanSearch,
} from "lucide-react";
import type { Identifier } from "ra-core";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import type {
  EchoflicksSessionMedia,
  ImageTriageDto,
  ImageTriageObservation,
} from "../../types";
import { MediaGalleryDialog } from "../MediaGalleryDialog";
import { useMediaUrl } from "../useMediaUrl";
import { IMAGE_TRIAGE_STATUS_COPY } from "./imageTriageCopy";
import {
  ImageTriageModeBadge,
  ImageTriageSuppressionSummary,
} from "./ImageTriageEnforcementStatus";
import { ImageTriageFilters } from "./ImageTriageFilters";
import { ImageTriageGroup } from "./ImageTriageGroup";
import { ImageTriageSummary } from "./ImageTriageSummary";
import { ImageTriageThumb } from "./ImageTriageThumb";
import {
  exactMediaByKey,
  failureFirst,
  filterResultCount,
  groupCategory,
  groupCategoryOrder,
  groupMatchesFilter,
  observationMatchesFilter,
  observationNeedsStandaloneReview,
  proposedMomentSets,
  type ImageTriageFilter,
} from "./imageTriageUtils";
import { useImageTriage } from "./useImageTriage";

export function ImagePreprocessingSection({
  sessionId,
  media,
  canViewTechnical,
}: {
  sessionId: Identifier;
  media: EchoflicksSessionMedia[];
  canViewTechnical: boolean;
}) {
  const query = useImageTriage(sessionId);

  if (query.isPending) return <ImagePreprocessingLoading />;
  if (query.isError || !query.data) {
    return (
      <ImagePreprocessingUnavailable onRetry={() => void query.refetch()} />
    );
  }

  return (
    <ImagePreprocessingCard
      dto={query.data.data}
      media={media}
      canViewTechnical={canViewTechnical}
      onRefresh={() => void query.refetch()}
      refreshing={query.isFetching}
    />
  );
}

export function ImagePreprocessingCard({
  dto,
  media,
  canViewTechnical,
  onRefresh,
  refreshing = false,
}: {
  dto: ImageTriageDto;
  media: EchoflicksSessionMedia[];
  canViewTechnical: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  const [filter, setFilter] = useState<ImageTriageFilter>("needs_review");
  const [gallery, setGallery] = useState<{
    items: EchoflicksSessionMedia[];
    index: number | null;
  }>({ items: [], index: null });
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const mediaByKey = useMemo(() => exactMediaByKey(media), [media]);
  const allImageMedia = useMemo(
    () =>
      dto.observations
        .map((candidate) => mediaByKey.get(candidate.source.source_key))
        .filter((candidate): candidate is EchoflicksSessionMedia =>
          Boolean(candidate),
        ),
    [dto.observations, mediaByKey],
  );
  const visibleGroups = useMemo(
    () =>
      // Classical groups first, then AI-proposed moment sets (SIM-004) — the
      // synthesized `itadj_` cards render through the same group component.
      [...dto.groups, ...proposedMomentSets(dto)]
        .filter((group) => groupMatchesFilter(group, filter))
        .sort((left, right) => {
          const typeOrder =
            groupCategoryOrder(left) - groupCategoryOrder(right);
          return typeOrder || left.group_id.localeCompare(right.group_id);
        }),
    [dto, filter],
  );
  const groupedMemberIds = useMemo(
    () => new Set(visibleGroups.flatMap((group) => group.members)),
    [visibleGroups],
  );
  const visibleObservations = useMemo(
    () =>
      failureFirst(
        dto.observations.filter(
          (observation) =>
            observationMatchesFilter(observation, filter) &&
            (!groupedMemberIds.has(observation.occurrence_id) ||
              (filter === "needs_review" &&
                observationNeedsStandaloneReview(observation))),
        ),
      ),
    [dto.observations, filter, groupedMemberIds],
  );
  const resultCount = useMemo(
    () => filterResultCount(dto, filter),
    [dto, filter],
  );
  const status = IMAGE_TRIAGE_STATUS_COPY[dto.state];
  const partial = ["partial_analyzer", "partial_projection"].includes(
    dto.state,
  );

  useEffect(() => {
    if (gallery.index !== null && gallery.index >= gallery.items.length) {
      setGallery({ items: [], index: null });
    }
  }, [gallery.index, gallery.items.length]);

  const openGallery = (items: EchoflicksSessionMedia[], index: number) =>
    setGallery({ items, index });

  return (
    <Card id="image-preprocessing-section">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <ScanSearch className="size-4" aria-hidden />
              Image preprocessing
            </CardTitle>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Automated grouping and technical checks on the images submitted
              for this Echo.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={
                dto.state === "partial_analyzer" ? "destructive" : "outline"
              }
            >
              {status.badge}
            </Badge>
            <ImageTriageModeBadge dto={dto} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <ImageTriageSuppressionSummary dto={dto} />

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {status.description}
            </p>
            {partial ? (
              <p className="text-sm font-medium">
                {dto.summary?.analyzed_images ??
                  dto.projection.projected_observations}{" "}
                of{" "}
                {dto.summary?.source_images ??
                  dto.projection.expected_observations}{" "}
                images analyzed
              </p>
            ) : null}
          </div>
          {onRefresh &&
          ["pending", "partial_projection"].includes(dto.state) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
            >
              <RefreshCw
                className={
                  refreshing ? "animate-spin motion-reduce:animate-none" : ""
                }
              />
              Refresh
            </Button>
          ) : null}
        </div>

        <ImageTriageSummary dto={dto} />

        {dto.state === "pending" ? (
          <div
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6"
            aria-label="Image preprocessing is loading"
          >
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Skeleton key={item} className="aspect-square w-full" />
            ))}
          </div>
        ) : dto.observations.length ? (
          <>
            <ImageTriageFilters value={filter} onChange={setFilter} />
            <p className="sr-only" aria-live="polite">
              {resultCount.observations} images and {resultCount.groups} groups
              match {filter.replaceAll("_", " ")}.
            </p>

            {visibleObservations.length ? (
              <section
                className="space-y-3"
                aria-labelledby="image-triage-individual-heading"
              >
                <h4
                  id="image-triage-individual-heading"
                  className="font-medium"
                >
                  {filter === "all" ? "Other images" : "Image findings"}
                </h4>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {visibleObservations.map((observation) => {
                    const item = mediaByKey.get(observation.source.source_key);
                    const index = item
                      ? allImageMedia.findIndex(
                          (candidate) => candidate.id === item.id,
                        )
                      : -1;
                    return (
                      <ImageTriageThumb
                        key={observation.occurrence_id}
                        observation={observation}
                        media={item}
                        onOpen={
                          item && index >= 0
                            ? () => openGallery(allImageMedia, index)
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              </section>
            ) : null}

            <div className="space-y-3">
              {visibleGroups.map((group, index) => (
                <ImageTriageGroup
                  key={group.group_id}
                  group={group}
                  dto={dto}
                  media={media}
                  number={
                    visibleGroups
                      .slice(0, index + 1)
                      .filter(
                        (candidate) =>
                          groupCategory(candidate) === groupCategory(group),
                      ).length
                  }
                  onOpenGallery={openGallery}
                />
              ))}
            </div>

            {!visibleObservations.length && !visibleGroups.length ? (
              <div className="rounded-lg border border-dashed p-5 text-center text-sm text-muted-foreground">
                No images match this filter.
              </div>
            ) : null}
          </>
        ) : null}

        {dto.unmatched_observations.length ? (
          <UnmatchedObservations
            observations={dto.observations.filter((observation) =>
              dto.unmatched_observations.includes(observation.occurrence_id),
            )}
          />
        ) : null}

        {canViewTechnical && dto.report ? (
          <details
            className="rounded-lg border bg-muted/20 p-3"
            onToggle={(event) => setTechnicalOpen(event.currentTarget.open)}
          >
            <summary className="cursor-pointer text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Technical details
            </summary>
            {technicalOpen ? (
              <div className="mt-3 grid gap-3 text-xs text-muted-foreground sm:grid-cols-2">
                <TechnicalValue
                  label="Report ID"
                  value={dto.report.report_id}
                />
                <TechnicalValue
                  label="Report digest"
                  value={dto.report.report_digest}
                />
                <TechnicalValue
                  label="Config release"
                  value={dto.report.config?.release_id}
                />
                <TechnicalValue
                  label="Config digest"
                  value={dto.report.config?.digest}
                />
                <TechnicalValue
                  label="Analyzer"
                  value={dto.report.analyzer?.bundle_version}
                />
                <TechnicalValue
                  label="Completed"
                  value={dto.report.completed_at}
                />
                <div className="sm:col-span-2">
                  <ReportLink source={dto.report.source} />
                </div>
              </div>
            ) : null}
          </details>
        ) : null}
      </CardContent>

      <MediaGalleryDialog
        items={gallery.items}
        activeIndex={gallery.index}
        onActiveIndexChange={(index) =>
          setGallery((current) => ({ ...current, index }))
        }
        title="Image preprocessing gallery"
      />
    </Card>
  );
}

function UnmatchedObservations({
  observations,
}: {
  observations: ImageTriageObservation[];
}) {
  return (
    <Alert>
      <AlertTriangle aria-hidden />
      <AlertTitle>Unmatched observations</AlertTitle>
      <AlertDescription>
        <p>
          {observations.length}{" "}
          {observations.length === 1
            ? "observation could"
            : "observations could"}{" "}
          not be matched to an exact uploaded-media key. No badges were attached
          to a guessed thumbnail.
        </p>
        <ul className="list-disc pl-4 font-mono text-xs">
          {observations.map((observation) => (
            <li key={observation.occurrence_id}>
              {observation.occurrence_id} · upload{" "}
              {observation.source.upload_sequence + 1}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

type ImageTriageReportSource = NonNullable<
  NonNullable<ImageTriageDto["report"]>["source"]
>;

function ReportLink({ source }: { source?: ImageTriageReportSource | null }) {
  const media = useMediaUrl(
    source
      ? {
          storage_bucket: source.bucket,
          storage_path: source.s3_key,
          storage_is_public: false,
        }
      : null,
    { expiresIn: 10 * 60 },
  );

  if (!source) return <p>Full report pointer unavailable.</p>;
  if (media.url) {
    return (
      <Button asChild variant="outline" size="sm">
        <a href={media.url} target="_blank" rel="noreferrer">
          Open full report
          <ExternalLink className="size-3.5" aria-hidden />
        </a>
      </Button>
    );
  }
  if (media.status === "error") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span>The full report link could not be prepared.</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={media.refresh}
        >
          Try again
        </Button>
      </div>
    );
  }
  return <span>Preparing full report link…</span>;
}

function TechnicalValue({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <p className="font-medium text-foreground">{label}</p>
      <p className="break-all font-mono">{value}</p>
    </div>
  );
}

function ImagePreprocessingLoading() {
  return (
    <Card id="image-preprocessing-section" aria-busy="true">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <ScanSearch className="size-4" aria-hidden />
            Image preprocessing
          </CardTitle>
          <Badge variant="outline">Processing</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-4 w-3/4" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Skeleton key={item} className="aspect-square w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ImagePreprocessingUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <Card id="image-preprocessing-section">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="size-4" aria-hidden />
            Image preprocessing
          </CardTitle>
          <Badge variant="outline">Unavailable</Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
        <p>Image preprocessing could not be loaded for this Echo.</p>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw />
          Try again
        </Button>
      </CardContent>
    </Card>
  );
}
